/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useState, useMemo } from "react";
import { listCOTReports } from "../../api/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import PageBackButton from "../../components/PageBackButton";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line,
} from "recharts";

// Popular forex-related COT markets
const MARKET_PRESETS = [
  "EURO FX",
  "JAPANESE YEN",
  "BRITISH POUND",
  "SWISS FRANC",
  "CANADIAN DOLLAR",
  "AUSTRALIAN DOLLAR",
  "NEW ZEALAND DOLLAR",
  "U.S. DOLLAR INDEX",
  "GOLD",
  "SILVER",
  "CRUDE OIL",
  "NATURAL GAS",
];

function getDefaultDateRange() {
  const now = new Date();
  const threeMonthsAgo = new Date(now);
  threeMonthsAgo.setMonth(now.getMonth() - 3);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(threeMonthsAgo), to: fmt(now) };
}

export default function COTReportPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [market, setMarket] = useState("");

  // View mode
  const [viewMode, setViewMode] = useState("table"); // "table" | "chart"

  useEffect(() => {
    const { from, to } = getDefaultDateRange();
    setDateFrom(from);
    setDateTo(to);
    fetchReports(from, to, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReports = async (from, to, mkt, pg) => {
    try {
      setLoading(true);
      setError(null);
      const currentPage = pg ?? page;
      const res = await listCOTReports({
        date_from: from || dateFrom,
        date_to: to || dateTo,
        symbol: mkt ?? market,
        page: currentPage,
        page_size: pageSize,
      });
      // Paginated response: { count, next, previous, results }
      if (res && typeof res.count === "number" && Array.isArray(res.results)) {
        setReports(res.results);
        setTotalCount(res.count);
      } else {
        const items = Array.isArray(res) ? res : [];
        setReports(items);
        setTotalCount(items.length);
      }
    } catch (e) {
      setError(e.message || "Failed to load COT data");
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = (preset) => {
    setMarket(preset);
    setPage(1);
    fetchReports(dateFrom, dateTo, preset, 1);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
    fetchReports(dateFrom, dateTo, market, p);
  };

  // Prepare chart data — sorted by date ascending
  const chartData = useMemo(() => {
    return [...reports]
      .sort((a, b) => (a.as_of_date < b.as_of_date ? -1 : 1))
      .map((r) => ({
        date: r.as_of_date,
        "Noncomm Long": r.noncomm_long_all,
        "Noncomm Short": r.noncomm_short_all,
        "Comm Long": r.comm_long_all,
        "Comm Short": r.comm_short_all,
        "Open Interest": r.open_interest_all,
        netNoncomm: r.noncomm_long_all - r.noncomm_short_all,
        netComm: r.comm_long_all - r.comm_short_all,
      }));
  }, [reports]);

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start gap-3">
        <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
        <div>
          <h1 className="text-2xl font-semibold">COT Report</h1>
          <p className="text-sm text-textSecondary">
            Commitments of Traders — weekly positioning data from the CFTC
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">Market</label>
            <Input
              placeholder="e.g. EURO FX"
              value={market}
              onChange={(e) => setMarket(e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2">Quick Select</label>
          <div className="flex flex-wrap gap-2">
            {MARKET_PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant={market === preset ? "default" : "secondary"}
                size="sm"
                onClick={() => handlePresetClick(preset)}
              >
                {preset}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={() => { setPage(1); fetchReports(dateFrom, dateTo, market, 1); }}>Apply</Button>
          <Button variant="secondary" onClick={() => { setMarket(""); setPage(1); }}>Clear Market</Button>
          <div className="ml-auto flex gap-2">
            <Button
              variant={viewMode === "table" ? "default" : "secondary"}
              size="sm"
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              variant={viewMode === "chart" ? "default" : "secondary"}
              size="sm"
              onClick={() => setViewMode("chart")}
            >
              Chart
            </Button>
          </div>
        </div>
      </Card>

      {/* Chart View */}
      {viewMode === "chart" && (
        <Card className="p-4 space-y-4">
          <h2 className="text-lg font-medium">Net Positioning Over Time</h2>
          {chartData.length === 0 ? (
            <p className="text-textSecondary text-sm">
              {loading ? "Loading..." : "No data to chart. Select a market and apply filters."}
            </p>
          ) : (
            <>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="netNoncomm" name="Net Non-Commercial" stroke="#10b981" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="netComm" name="Net Commercial" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <h2 className="text-lg font-medium">Open Interest</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="Open Interest" fill="#6366f1" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Market</th>
                  <th className="text-right p-3">Open Interest</th>
                  <th className="text-right p-3">Noncomm Long</th>
                  <th className="text-right p-3">Noncomm Short</th>
                  <th className="text-right p-3">Net Noncomm</th>
                  <th className="text-right p-3">Comm Long</th>
                  <th className="text-right p-3">Comm Short</th>
                  <th className="text-right p-3">Net Comm</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td className="p-3" colSpan={9}>Loading...</td></tr>
                )}
                {error && !loading && (
                  <tr><td className="p-3 text-red-400" colSpan={9}>{error}</td></tr>
                )}
                {!loading && !error && reports.length === 0 && (
                  <tr><td className="p-3" colSpan={9}>No COT data found</td></tr>
                )}
                {!loading && !error && reports.map((r) => {
                  const netNoncomm = r.noncomm_long_all - r.noncomm_short_all;
                  const netComm = r.comm_long_all - r.comm_short_all;
                  return (
                    <tr key={r.id} className="border-t border-border/40 hover:bg-secondary/20">
                      <td className="p-3 whitespace-nowrap">{r.as_of_date}</td>
                      <td className="p-3 text-xs">{r.market_and_exchange_names}</td>
                      <td className="p-3 text-right">{r.open_interest_all?.toLocaleString()}</td>
                      <td className="p-3 text-right text-green-400">{r.noncomm_long_all?.toLocaleString()}</td>
                      <td className="p-3 text-right text-red-400">{r.noncomm_short_all?.toLocaleString()}</td>
                      <td className={`p-3 text-right font-medium ${netNoncomm >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {netNoncomm?.toLocaleString()}
                      </td>
                      <td className="p-3 text-right text-green-400">{r.comm_long_all?.toLocaleString()}</td>
                      <td className="p-3 text-right text-red-400">{r.comm_short_all?.toLocaleString()}</td>
                      <td className={`p-3 text-right font-medium ${netComm >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {netComm?.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/40">
              <span className="text-sm text-textSecondary">
                {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} records
              </span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(1)} className="px-2">First</Button>
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)} className="px-2">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {(() => {
                  const pages = [];
                  let start = Math.max(1, page - 2);
                  let end = Math.min(totalPages, start + 4);
                  start = Math.max(1, end - 4);
                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <Button key={i} variant={i === page ? "default" : "ghost"} size="sm" className="px-3 min-w-[2rem]" onClick={() => goToPage(i)}>{i}</Button>
                    );
                  }
                  return pages;
                })()}
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(page + 1)} className="px-2">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => goToPage(totalPages)} className="px-2">Last</Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
